<template>
  <div class="space-y-3">
    <h3 class="text-sm font-medium text-gray-900 dark:text-white">{{ titleLabel }}</h3>
    <div class="grid grid-cols-8 gap-2">
      <button
        type="button"
        v-for="color in colors"
        :key="color.value"
        @click="selectColor(color.value)"
        class="w-8 h-8 rounded border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        :class="[
          selectedColor === color.value
            ? 'border-gray-900 dark:border-white shadow-lg scale-110'
            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        ]"
        :style="{ backgroundColor: color.value }"
        :title="color.label"
      >
        <div v-if="selectedColor === color.value" class="w-full h-full flex items-center justify-center">
          <svg class="w-4 h-4 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
      </button>
    </div>
    
    <!-- Custom Color Input -->
    <div class="mt-3">
      <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{{ t('colors.custom') }}</label>
      <div class="flex items-center space-x-2">
        <input 
          v-model="customColor"
          type="color" 
          class="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
          @input="selectColor(customColor)"
        >
        <input 
          v-model="customColor"
          type="text" 
          placeholder="#3B82F6"
          class="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          @input="selectColor(customColor)"
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: String,
  colorContext: {
    type: String,
    default: 'lead',
    validator: (v) => ['lead', 'workspace'].includes(v)
  }
})

const { t } = useI18n()

const titleLabel = computed(() =>
  props.colorContext === 'workspace' ? t('colors.workspaceTitle') : t('colors.leadTitle')
)

const emit = defineEmits(['update:modelValue'])

const selectedColor = ref(props.modelValue || '#3B82F6')
const customColor = ref(props.modelValue || '#3B82F6')

const colorDefs = [
  { value: '#3B82F6', nameKey: 'blue' },
  { value: '#4285F4', nameKey: 'blueGoogle' },
  { value: '#10B981', nameKey: 'green' },
  { value: '#34A853', nameKey: 'greenGoogle' },
  { value: '#F59E0B', nameKey: 'yellow' },
  { value: '#FBBC05', nameKey: 'yellowGoogle' },
  { value: '#F7F700', nameKey: 'yellowNeon' },
  { value: '#EF4444', nameKey: 'red' },
  { value: '#EA4335', nameKey: 'redGoogle' },
  { value: '#8B5CF6', nameKey: 'purple' },
  { value: '#EC4899', nameKey: 'pink' },
  { value: '#06B6D4', nameKey: 'cyan' },
  { value: '#84CC16', nameKey: 'lime' },
  { value: '#F97316', nameKey: 'orange' },
  { value: '#f26202', nameKey: 'orangeStrong' },
  { value: '#6B7280', nameKey: 'gray' },
  { value: '#1F2937', nameKey: 'black' },
  { value: '#000000', nameKey: 'blackPure' },
  { value: '#FFFFFF', nameKey: 'white' },
  { value: '#FBBF24', nameKey: 'goldYellow' },
  { value: '#34D399', nameKey: 'emeraldGreen' },
  { value: '#A78BFA', nameKey: 'lightPurple' },
  { value: '#FB7185', nameKey: 'lightPink' },
  { value: '#22D3EE', nameKey: 'lightCyan' },
  { value: '#A3E635', nameKey: 'lightLime' },
  { value: '#FB923C', nameKey: 'lightOrange' },
  { value: '#9CA3AF', nameKey: 'lightGray' },
  { value: '#374151', nameKey: 'darkGray' },
  { value: '#FDE047', nameKey: 'paleYellow' },
  { value: '#86EFAC', nameKey: 'paleGreen' },
  { value: '#C4B5FD', nameKey: 'palePurple' }
]

const colors = computed(() =>
  colorDefs.map((c) => ({
    ...c,
    label: t(`colors.names.${c.nameKey}`)
  }))
)

const selectColor = (color) => {
  selectedColor.value = color
  customColor.value = color
  emit('update:modelValue', color)
}

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    selectedColor.value = newValue
    customColor.value = newValue
  }
})
</script>